import React from 'react';
import { Link } from 'react-router-dom';
import {
  RocketLaunchIcon,
  HeartIcon,
  GlobeAmericasIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import Header from './app/Header';
import Footer from './app/Footer';
import FooterCopyright from './app/FooterCopyright';
import { SHARED } from './utils/product';

const valores = [
  { icon: RocketLaunchIcon, title: 'Rota, não corrida', desc: 'Circuitos diários compartilhados no lugar de um carro exclusivo ocioso.' },
  { icon: HeartIcon, title: 'Preço previsível', desc: 'Plano mensal. Sem simulador de picape, sem surpresa de km.' },
  { icon: GlobeAmericasIcon, title: 'Local', desc: 'Nascemos em Goiânia. Operamos a Grande Goiânia.' },
  { icon: SparklesIcon, title: 'Disciplina na porta', desc: `Encontre o veículo em até ${SHARED.doorWait}. É assim que o plano fica barato.` },
];

const Sobre = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-mesh-light" aria-hidden="true" />
        <div className="relative container mx-auto px-4 lg:px-6 py-16 lg:py-24">
          <div className="max-w-3xl">
            <span className="badge-brand mb-5">Sobre nós</span>
            <h1 className="font-display text-display-xl text-ink-900">
              Rotas B2B,{' '}
              <span className="bg-clip-text text-transparent bg-brand-gradient">feitas em Goiânia</span>
            </h1>
            <p className="mt-5 text-xl text-ink-500 leading-relaxed">
              A Fretz existe para empresas que hoje pagam caro por carro, motorista
              e rota ruim. Operamos uma frota identificada em circuitos compartilhados
              na Grande Goiânia: sacolas e pacotes, planos Diário, Semanal e Mensal.
            </p>
          </div>
        </div>
      </section>

      <section className="section-tight bg-ink-50">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { value: SHARED.fromPrice, label: 'Plano a partir de' },
              { value: '3', label: 'Ritmos de coleta' },
              { value: SHARED.doorWait, label: 'Espera máxima' },
              { value: SHARED.coverage, label: 'Cobertura' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center p-6">
                <p className="font-display text-2xl lg:text-3xl font-extrabold text-brand-700">{value}</p>
                <p className="text-ink-500 text-sm mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="eyebrow mb-3">Nossa missão</p>
              <h2 className="font-display text-display-lg text-ink-900">
                Tirar da frente o custo de uma frota ineficiente
              </h2>
              <p className="mt-5 text-ink-500 text-lg leading-relaxed">
                Lojas de roupa, costureiras, e-commerces de sacola, confeitarias,
                distribuição de kits e escritórios de malote não precisam de caminhão
                nem de um motorista parado no trânsito. Precisam de rota.
              </p>
              <p className="mt-4 text-ink-500 text-lg leading-relaxed">
                Não fazemos mudança, picape avulsa nem marketplace de caminhão.
                Fazemos o plano mensal e o carro Fretz passa.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-4xl bg-brand-gradient aspect-[4/3] p-10 shadow-glow flex items-center justify-center">
                <div className="text-white text-center">
                  <RocketLaunchIcon className="h-20 w-20 mx-auto mb-4 opacity-80" />
                  <p className="font-display text-2xl font-bold">Movendo a Grande Goiânia</p>
                  <p className="text-white/80 mt-1">rotas B2B · pacotes leves</p>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 h-24 w-24 rounded-2xl bg-accent-400 -z-0" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-ink-50">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-2">Nossos valores</p>
            <h2 className="font-display text-display-lg text-ink-900">O que nos move</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {valores.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="card p-6">
                <div className="h-12 w-12 rounded-2xl bg-brand-gradient text-white flex items-center justify-center shadow-glow">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-bold text-ink-900 mt-4">{title}</h3>
                <p className="text-ink-500 text-sm mt-2">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="carreiras" className="section bg-white">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="rounded-4xl bg-ink-900 p-10 lg:p-14 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-mesh-dark opacity-50" aria-hidden="true" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="eyebrow !text-accent-400 mb-3">Carreiras</p>
                <h2 className="font-display text-3xl lg:text-4xl font-bold">
                  Trabalhe nas rotas Fretz
                </h2>
                <p className="mt-4 text-ink-300 text-lg">
                  Procuramos gente para dirigir os circuitos e para construir o produto.
                </p>
              </div>
              <div className="lg:text-right flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
                <Link to="/quero-ser-entregador" className="btn-accent">
                  Candidatar-se às rotas
                </Link>
                <a href="mailto:carreiras@fretz.com.br" className="btn-secondary">
                  Outras vagas
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="imprensa" className="section-tight bg-white">
        <div className="container mx-auto px-4 lg:px-6 text-center">
          <p className="eyebrow mb-2">Imprensa</p>
          <h3 className="font-display text-2xl font-bold text-ink-900">Quer falar com a gente?</h3>
          <p className="mt-2 text-ink-500">
            Envie um e-mail para <a href="mailto:imprensa@fretz.com.br" className="text-brand-700 underline">imprensa@fretz.com.br</a>.
          </p>
          <Link to="/" className="btn-primary mt-6">Voltar para a home</Link>
        </div>
      </section>

      <Footer />
      <FooterCopyright />
    </div>
  );
};

export default Sobre;
